import jsPDF from "jspdf";
import "jspdf-autotable";
import { EXPORT_FILE_NAME, TABLE_HEADERS, STATUS } from "./constants";

/**
 * Utility functions for product availability operations
 * Provides pure functions for data transformation and export
 */

/**
 * Get current date time formatted as DD/MM/YYYY
 * @returns {string} Formatted date string
 */
export const getCurrentDateTime = () => {
  const now = new Date();
  return `${String(now.getDate()).padStart(2, "0")}/${String(
    now.getMonth() + 1,
  ).padStart(2, "0")}/${now.getFullYear()}`;
};

/**
 * Flatten products with variants into a single array for display
 * Handles both products with variants and without variants
 *
 * @param {Array} products - Products array
 * @returns {Array} Flattened product rows
 */
export const flattenProducts = (products) => {
  return products.flatMap((product) => {
    if (product.variants && product.variants.length > 0) {
      return product.variants.map((variant) => ({
        id: `${product._id}-${variant._id}`,
        productNumber: product.productNumber || "",
        productName: `${product.productName || ""}`,
        categoryName: product.categoryName || "",
        subcategoryName: product.subcategoryName || "",
        price: variant.price ?? "-",
        offerPrice: variant.offerPrice ?? "-",
        status:
          variant.isVisible || product.isVisible
            ? STATUS.AVAILABLE
            : STATUS.NOT_AVAILABLE,
      }));
    } else {
      return [
        {
          id: product._id,
          productNumber: product.productNumber || "",
          productName: product.productName || "",
          categoryName: product.categoryName || "",
          subcategoryName: product.subcategoryName || "",
          price: product.price ?? "-",
          offerPrice: product.offerPrice ?? "-",
          status: product.isVisible ? STATUS.AVAILABLE : STATUS.NOT_AVAILABLE,
        },
      ];
    }
  });
};

/**
 * Filter products based on search query
 * Searches across product name, number, category, and subcategory
 *
 * @param {Array} products - Products array
 * @param {string} searchQuery - Search query string
 * @returns {Array} Filtered products
 */
export const filterProducts = (products, searchQuery) => {
  if (!searchQuery) return products;

  const query = searchQuery.toLowerCase();
  return products.filter(
    (product) =>
      product.productName.toLowerCase().includes(query) ||
      product.productNumber.toLowerCase().includes(query) ||
      product.categoryName.toLowerCase().includes(query) ||
      product.subcategoryName.toLowerCase().includes(query),
  );
};

/**
 * Export product availability to PDF
 *
 * @param {Array} products - Flattened products array
 * @param {string} restaurantName - Restaurant name
 * @param {string} currencySymbol - Currency symbol
 */
export const exportAvailability = (products, restaurantName, currencySymbol) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(31, 41, 55);
  const nameText = restaurantName || "Restaurant";
  const textWidth = doc.getTextWidth(nameText);
  doc.text(nameText, (pageWidth - textWidth) / 2, 22);

  doc.setFontSize(14);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(107, 114, 128);
  const reportTitle = "Product Availability";
  const titleWidth = doc.getTextWidth(reportTitle);
  doc.text(reportTitle, (pageWidth - titleWidth) / 2, 30);

  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.5);
  doc.line(14, 35, pageWidth - 14, 35);

  const barHeight = 12;

  doc.setFillColor(249, 250, 251);
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.5);
  doc.rect(14, 40, pageWidth - 28, barHeight, "FD");

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(55, 65, 81);

  doc.text(`Date:`, 18, 47.5);
  doc.setFont("helvetica", "normal");
  doc.text(getCurrentDateTime(), 30, 47.5);

  doc.setFont("helvetica", "bold");
  const middleStart = pageWidth / 2 - 30;
  doc.text(`Total Products:`, middleStart, 47.5);
  doc.setFont("helvetica", "normal");
  doc.text(`${products.length}`, middleStart + 32, 47.5);

  const availableCount = products.filter((p) => p.status === STATUS.AVAILABLE).length;
  const notAvailableCount = products.filter((p) => p.status === STATUS.NOT_AVAILABLE).length;

  const rightEnd = pageWidth - 18;

  doc.setFont("helvetica", "bold");
  doc.setTextColor(55, 65, 81);
  const notAvailLabel = "Not Available: ";
  const notAvailLabelWidth = doc.getTextWidth(notAvailLabel);
  const notAvailValueWidth = doc.getTextWidth(`${notAvailableCount}`);

  const notAvailStartX = rightEnd - notAvailValueWidth - notAvailLabelWidth;
  doc.text(notAvailLabel, notAvailStartX, 47.5);
  
  doc.setFont("helvetica", "normal");
  doc.setTextColor(220, 38, 38); // Red
  doc.text(`${notAvailableCount}`, notAvailStartX + notAvailLabelWidth, 47.5);

  doc.setFont("helvetica", "bold");
  doc.setTextColor(55, 65, 81);
  const availLabel = "Available: ";
  const availLabelWidth = doc.getTextWidth(availLabel);
  const availValueWidth = doc.getTextWidth(`${availableCount}`);

  const availStartX = notAvailStartX - availValueWidth - availLabelWidth - 10;
  doc.text(availLabel, availStartX, 47.5);

  doc.setFont("helvetica", "normal");
  doc.setTextColor(22, 163, 74); // Green
  doc.text(`${availableCount}`, availStartX + availLabelWidth, 47.5);

  // Restore color for table
  doc.setTextColor(55, 65, 81);

  const tableData = products.map((item) => [
    item.productNumber,
    item.productName,
    item.categoryName,
    item.subcategoryName,
    item.price,
    item.offerPrice,
    item.status,
  ]);

  doc.autoTable({
    startY: 55,
    head: [
      [
        TABLE_HEADERS.PRODUCT_NUMBER,
        TABLE_HEADERS.PRODUCT_NAME,
        TABLE_HEADERS.CATEGORY,
        TABLE_HEADERS.SUBCATEGORY,
        TABLE_HEADERS.PRICE,
        TABLE_HEADERS.OFFER_PRICE,
        TABLE_HEADERS.STATUS,
      ],
    ],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: [31, 41, 55],
      textColor: 255,
      fontStyle: 'bold',
      halign: 'center'
    },
    columnStyles: {
      0: { halign: 'center' },
      1: { halign: 'left' },
      2: { halign: 'center' },
      3: { halign: 'center' },
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'center' },
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251]
    },
    styles: {
      fontSize: 9,
      cellPadding: 4,
      textColor: [55, 65, 81],
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
    },
    didParseCell: function (data) {
      if (data.section === 'body' && data.column.index === 6) {
        if (data.cell.raw === STATUS.AVAILABLE) {
          data.cell.styles.textColor = [22, 163, 74];
          data.cell.styles.fontStyle = 'bold';
        } else if (data.cell.raw === STATUS.NOT_AVAILABLE) {
          data.cell.styles.textColor = [220, 38, 38];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    }
  });

  const pageCount = doc.internal.getNumberOfPages();
  doc.setFontSize(8);
  doc.setTextColor(156, 163, 175);
  const generationDate = new Date().toLocaleString();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text(`Generated on ${generationDate}`, 14, doc.internal.pageSize.getHeight() - 10);
    const pageText = `Page ${i} of ${pageCount}`;
    const pageTextWidth = doc.getTextWidth(pageText);
    doc.text(pageText, pageWidth - 14 - pageTextWidth, doc.internal.pageSize.getHeight() - 10);
  }

  const currentDate = new Date().toISOString().split('T')[0];
  doc.save(`${EXPORT_FILE_NAME.replace('.pdf', '')}-${currentDate}.pdf`);
};

/**
 * Export product availability to CSV
 *
 * @param {Array} products - Flattened products array
 */
export const exportAvailabilityAsCSV = (products) => {
  // Create CSV header
  const headers = [
    TABLE_HEADERS.PRODUCT_NUMBER,
    TABLE_HEADERS.PRODUCT_NAME,
    TABLE_HEADERS.CATEGORY,
    TABLE_HEADERS.SUBCATEGORY,
    TABLE_HEADERS.PRICE,
    TABLE_HEADERS.OFFER_PRICE,
    TABLE_HEADERS.STATUS,
  ];

  // Create CSV rows
  const rows = products.map((item) => [
    item.productNumber,
    item.productName,
    item.categoryName,
    item.subcategoryName,
    item.price,
    item.offerPrice,
    item.status,
  ]);

  // Combine headers and rows
  const csvContent = [
    headers.join(","),
    ...rows.map((row) =>
      row
        .map((cell) => {
          // Escape quotes and wrap cells with commas in quotes
          const escapedCell = String(cell).replace(/"/g, '""');
          return `"${escapedCell}"`;
        })
        .join(","),
    ),
  ].join("\n");

  // Create blob and download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `Product_Availability_${getCurrentDateTime().replace(/\//g, "-")}.csv`,
  );
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Paginate data for display
 *
 * @param {Array} data - Data array
 * @param {number} page - Current page (1-indexed)
 * @param {number} rowsPerPage - Rows per page
 * @returns {Array} Paginated data
 */
export const paginateData = (data, page, rowsPerPage) => {
  return data.slice((page - 1) * rowsPerPage, page * rowsPerPage);
};
